import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useSilverVersions, useStartValidationRun, useValidationRun } from "utils";

import { POLL_TIMEOUT_MS, RERUN_CONFIRM_DESCRIPTION, RERUN_CONFIRM_TITLE, RERUN_HINT, SOIL_PROPERTY_METRICS } from "consts";

import { ValidationRow, versionOptionLabel } from "models";

import { AlertBanner, Button, Flex, Option, Popconfirm, Select, Tabs, Tooltip } from "components";

import { CachedBadge, DurationBadge } from "pages/admin/soilSight/components/multiple/SoilSightMultipleVersionOutcomes.tsx";
import { ErrorBanner } from "pages/admin/soilSight/components/shared/ErrorBanner.tsx";
import { ValidationDetail } from "pages/admin/soilSight/components/shared/ValidationDetail.tsx";
import { ValidationLocations } from "pages/admin/soilSight/components/shared/ValidationLocations.tsx";
import { SingleVersionCharts } from "pages/admin/soilSight/components/single/SingleVersionCharts.tsx";
import { SingleVersionCommentModal } from "pages/admin/soilSight/components/single/SingleVersionCommentModal.tsx";
import {
  SingleVersionLocationRotationTable,
  SingleVersionLocationTable,
  SingleVersionTable,
} from "pages/admin/soilSight/components/single/SingleVersionTable.tsx";
import { useOnNavigationSearchParams } from "pages/admin/soilSight/hooks/useOnNavigationSearchParams";
import { computeAverages, computeLocationAverages, computeLocationRotationAverages } from "pages/admin/soilSight/utils/compareUtils.ts";
import { readSingleVersionSelection, writeSingleVersionSelection } from "pages/admin/soilSight/utils/slotLocalStorage.ts";

export const SoilSightSingleVersionOutcomes = () => {
  const { data: silverVersions } = useSilverVersions();
  const versions = useMemo(() => silverVersions ?? [], [silverVersions]);

  const [version, setVersion] = useState<string | null>(() => readSingleVersionSelection());
  // The version the currently shown results (if any) were actually run for — diverges from `version`
  // as soon as the dropdown changes, since selecting a version never auto-runs it.
  const [ranForVersion, setRanForVersion] = useState<string | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [wasCached, setWasCached] = useState<boolean | null>(null);
  const [isCommentOpen, setIsCommentOpen] = useState(false);

  const startMutation = useStartValidationRun();
  const { data: runData } = useValidationRun(runId, startedAt);

  const runOne = useCallback(
    (versionTag: string, forceRerun = false) => {
      setStartError(null);
      setRanForVersion(versionTag);
      startMutation.mutate(
        { versionTag, forceRerun },
        {
          onSuccess: ({ runId: id, cached }) => {
            setRunId(id);
            setStartedAt(Date.now());
            setWasCached(cached);
          },
          onError: () => setStartError(`Failed to start run for ${versionTag}`),
        }
      );
    },
    [startMutation]
  );

  const handleRun = useCallback(() => {
    if (version) runOne(version);
  }, [version, runOne]);

  const handleRerun = useCallback(() => {
    if (version) runOne(version, true);
  }, [version, runOne]);

  // Remember the last selection so a reload restores the dropdown (does not auto-run).
  useEffect(() => {
    writeSingleVersionSelection(version);
  }, [version]);

  // Deep-link from the reviews table: preselect the version (does not auto-run).
  useOnNavigationSearchParams((params) => {
    const deepLinkVersion = params.get("version");
    if (deepLinkVersion) setVersion(deepLinkVersion);
  });

  const rows = useMemo<ValidationRow[] | null>(() => (runData?.status === "completed" ? runData.result ?? [] : null), [runData]);
  const failedMessage = runData?.status === "failed" ? runData.error ?? "Run failed" : null;
  const selectionOutOfSync = !!version && version !== ranForVersion;

  const [now, setNow] = useState(() => Date.now());
  const timedOut = !!startedAt && !rows && !failedMessage && now - startedAt > POLL_TIMEOUT_MS;
  const isRunning = startMutation.isPending || (!!runId && !rows && !failedMessage && !timedOut);

  // Tick only while a run is in flight so the timeout is detected; stops once nothing is busy (including after a timeout).
  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(id);
  }, [isRunning]);

  const tabs = useMemo(() => {
    if (!rows) return [];
    const averages = computeAverages(rows);
    const locationAverages = computeLocationAverages(rows);
    const locationRotationAverages = computeLocationRotationAverages(rows);
    return [
      {
        key: "compare",
        label: "CM and Model outcomes",
        children: (
          <>
            <SingleVersionTable rows={averages} />
          </>
        ),
      },
      { key: "charts", label: "CM and Model outcomes (Charts)", children: <SingleVersionCharts rows={rows} /> },
      {
        key: "soil-table",
        label: "Soil properties",
        children: (
          <Flex gap={"2rem"} vertical={true}>
            <SingleVersionLocationTable rows={locationAverages} version={version} />
            <SingleVersionLocationRotationTable rows={locationRotationAverages} version={version} />
          </Flex>
        ),
      },
      { key: "sp-charts", label: "Soil Props (Charts)", children: <SingleVersionCharts rows={rows} metrics={SOIL_PROPERTY_METRICS} /> },
      { key: "detail", label: "Detail", children: <ValidationDetail rows={rows} /> },
      { key: "gee", label: "GEE Locations", children: <ValidationLocations rows={rows} /> },
    ];
  }, [rows, version]);

  return (
    <div>
      <div className="soil-sight-validation-toolbar">
        <Select label="Version" style={{ width: 280 }} value={version ?? undefined} onChange={setVersion}>
          {versions.map((v) => (
            <Option key={v.version} value={v.version}>
              {versionOptionLabel(v)}
            </Option>
          ))}
        </Select>
        <Flex gap="1rem">
          <Button type="primary" disabled={!version || isRunning} loading={isRunning} onClick={handleRun}>
            Run validation
          </Button>
          <Tooltip title={RERUN_HINT}>
            <Popconfirm color="white" title={RERUN_CONFIRM_TITLE} description={RERUN_CONFIRM_DESCRIPTION} onConfirm={handleRerun} disabled={!rows}>
              <Button type="primary" disabled={!rows}>
                Re-run
              </Button>
            </Popconfirm>
          </Tooltip>
          <Button type="primary" onClick={() => setIsCommentOpen(true)} disabled={!version}>
            Add comment
          </Button>
        </Flex>
        {wasCached !== null && rows && (
          <Flex gap="0.5rem">
            <CachedBadge cached={wasCached} />
            <DurationBadge startedAt={runData?.startedAt} completedAt={runData?.completedAt} />
          </Flex>
        )}
      </div>

      {startError && <ErrorBanner message={startError} />}
      {failedMessage && <ErrorBanner message={failedMessage} />}
      {timedOut && <ErrorBanner message="Run timed out after 10 minutes. Run again to retry." />}

      {isRunning && <div className="soil-sight-validation-compare-empty-hint">Running scenarios — this can take several minutes on a fresh version.</div>}

      {!version && <div className="soil-sight-validation-compare-empty-hint">Select a version, then run validation.</div>}

      {selectionOutOfSync && !isRunning && (
        <AlertBanner
          type="warning"
          description={
            rows
              ? `Showing results for ${ranForVersion}, not the currently selected ${version}. Click "Run validation" to refresh.`
              : `${version} has not been run yet. Click "Run validation" to see its results.`
          }
        />
      )}

      {version && rows && rows.length === 0 && <ErrorBanner message="No rows found for this version." />}

      {rows && rows.length > 0 && <Tabs defaultActiveKey="compare" size="small" items={tabs} />}

      {version && <SingleVersionCommentModal open={isCommentOpen} onClose={() => setIsCommentOpen(false)} version={version} />}
    </div>
  );
};
